<script setup lang="ts">
import { useOIDCStore } from '@/stores/oidc.store'
import RevolveLight from '@/svg/logo/full/revolve-labs-logo-black.svg'
import RevolveDark from '@/svg/logo/full/revolve-labs-logo-white.svg'
import RevolveMarkLight from '@/svg/logo/mark/revolve-labs-logo-mark-black.svg'
import RevolveMarkDark from '@/svg/logo/mark/revolve-labs-logo-mark-white.svg'
import { Cpu, DataLine, OfficeBuilding, Setting } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { useDark } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

// Theme state
const isDark = useDark()

const oidcStore = useOIDCStore()
const {
  isEOSUser,
  isRCAppUser,
  isHVLAppUser,
  isHVLManagerUser,
  isASICWAppUser,
  isManagerUser,
  isRcAppManagerUser,
  isASICWManagerUser,
  isCustomer,
  isAdminUser,
} = storeToRefs(oidcStore)

// Sidebar state
const isCollapse = defineModel<boolean>({ default: false })

// Router state
const defaultActive = computed<string>(() => router.currentRoute.value.name?.toString() || '')
const router = useRouter()

/**
 * Toggles sidebar between "collapsed" and "expanded" states.
 */
const toggleSidebar = () => {
  isCollapse.value = !isCollapse.value
}
</script>

<template>
  <el-menu
    class="sidebar fixed inset-y-0 z-[1999]"
    :router="true"
    :default-active="defaultActive"
    :collapse="isCollapse"
    :collapse-transition="false"
  >
    <header class="sidebar__header" :class="!isCollapse && 'is-expanded'">
      <template v-if="isCollapse">
        <RevolveMarkDark v-if="isDark" style="width: 24px; height: 24px" />
        <RevolveMarkLight v-else style="width: 24px; height: 24px" />
      </template>
      <template v-else>
        <RevolveDark v-if="isDark" />
        <RevolveLight v-else />
      </template>
      <el-button v-show="!isCollapse" class="collapse-button" link @click="toggleSidebar">
        <Icon icon="material-symbols:left-panel-close-outline" height="20" />
      </el-button>
    </header>

    <el-sub-menu v-if="isManagerUser" index="core">
      <template #title>
        <el-icon>
          <Setting />
        </el-icon>
        <span>Management</span>
      </template>
      <el-menu-item index="core-sites" :route="{ name: 'core-sites' }">Sites</el-menu-item>
      <el-menu-item index="core-customers" :route="{ name: 'core-customers' }">Customers</el-menu-item>
      <el-menu-item index="core-foreman-configuration" :route="{ name: 'core-foreman-configuration' }"
        >Foreman Configuration</el-menu-item
      >
    </el-sub-menu>
    <el-sub-menu v-if="isEOSUser" index="eos">
      <template #title>
        <el-icon>
          <Icon icon="material-symbols:bolt-outline" height="20" />
        </el-icon>
        <span>Energy Operations</span>
      </template>
      <el-menu-item index="eos-table" :route="{ name: 'eos-table' }">Table</el-menu-item>
      <el-sub-menu index="eos-site-map">
        <template #title>
          <span>Site Map</span>
        </template>
        <el-menu-item index="eos-hosting-clients" :route="{ name: 'eos-hosting-clients' }"
          >Hosting Clients</el-menu-item
        >
        <el-menu-item index="eos-site-map" :route="{ name: 'eos-site-map' }">Infrastructure</el-menu-item>
      </el-sub-menu>
      <el-sub-menu index="eos-downtime">
        <template #title>
          <span>Downtime</span>
        </template>
        <el-menu-item index="eos-downtime-events" :route="{ name: 'eos-downtime-events' }">Events</el-menu-item>
        <el-menu-item index="eos-downtime-client-summary" :route="{ name: 'eos-downtime-client-summary' }"
          >Client Summary</el-menu-item
        >
        <el-menu-item
          index="eos-downtime-infrastructure-summary"
          :route="{ name: 'eos-downtime-infrastructure-summary' }"
          >Infrastructure Summary</el-menu-item
        >
        <el-menu-item index="eos-downtime-site-dispatch" :route="{ name: 'eos-downtime-site-dispatch' }"
          >Site Dispatches</el-menu-item
        >
      </el-sub-menu>

      <el-menu-item index="eos-charts" :route="{ name: 'eos-charts' }">Charts</el-menu-item>
      <el-menu-item index="eos-reports" :route="{ name: 'eos-reports' }">Reports</el-menu-item>
      <el-sub-menu index="eos-thresholds">
        <template #title>
          <span>Thresholds</span>
        </template>
        <el-menu-item index="eos-lmp-thresholds" :route="{ name: 'eos-lmp-thresholds' }">LMP Thresholds</el-menu-item>
        <el-menu-item index="eos-nbpt-thresholds" :route="{ name: 'eos-nbpt-thresholds' }"
          >NBPT Thresholds</el-menu-item
        >
      </el-sub-menu>
      <el-sub-menu index="eos-auto-curtailment">
        <template #title>
          <span>Auto Curtailment</span>
        </template>
        <el-menu-item index="eos-auto-curtailment-variables" :route="{ name: 'eos-auto-curtailment-variables' }"
          >Curtailment Variables</el-menu-item
        >
        <el-menu-item
          index="eos-site-curtailment-configurations"
          :route="{ name: 'eos-site-curtailment-configurations' }"
          >Sites Configuration</el-menu-item
        >
      </el-sub-menu>
      <el-menu-item index="eos-ppa" :route="{ name: 'eos-ppa' }">Power Purchase Agreements</el-menu-item>
      <el-sub-menu index="demand-response">
        <template #title>
          <span>Demand Response</span>
        </template>
        <el-menu-item index="program-participation" :route="{ name: 'program-participation' }"
          >Program Participation</el-menu-item
        >
        <el-menu-item index="program-earnings" :route="{ name: 'program-earnings' }">Earnings Dashboard</el-menu-item>
        <el-menu-item index="eos-brdt" :route="{ name: 'eos-brdt' }">Baseline Reset Days Tracker</el-menu-item>
        <el-menu-item index="da-pr-performance-tracker" :route="{ name: 'da-pr-performance-tracker' }"
          >DA PR Performance Tracker</el-menu-item
        >
        <el-menu-item index="or-performance-tracker" :route="{ name: 'or-performance-tracker' }"
          >OR Performance Tracker</el-menu-item
        >
        <el-menu-item index="eos-baseline-simulator" :route="{ name: 'eos-baseline-simulator' }"
          >Baseline Simulator</el-menu-item
        >
      </el-sub-menu>
      <el-menu-item v-if="isAdminUser" index="eos-bill-estimates" :route="{ name: 'eos-bill-estimates' }"
        >Billing</el-menu-item
      >
    </el-sub-menu>
    <el-sub-menu v-if="isAdminUser" index="cpm">
      <template #title>
        <el-icon>
          <DataLine />
        </el-icon>
        <span>CPM</span>
      </template>
      <el-menu-item index="cpm-program-success" :route="{ name: 'cpm-program-success' }">
        Program Success
      </el-menu-item>
      <el-menu-item index="cpm-load-forecast" :route="{ name: 'cpm-load-forecast' }">Load Forecast</el-menu-item>
      <el-menu-item index="cpm-transmission-peak" :route="{ name: 'cpm-transmission-peak' }">
        Transmission Peak
      </el-menu-item>
      <el-menu-item index="cpm-program-management" :route="{ name: 'cpm-program-management' }">
        Program Management
      </el-menu-item>
      <el-menu-item index="cpm-customer-savings" :route="{ name: 'cpm-customer-savings' }">
        Customer Savings
      </el-menu-item>
    </el-sub-menu>
    <el-sub-menu v-if="isRCAppUser || isCustomer" index="rc">
      <template #title>
        <el-icon>
          <Cpu />
        </el-icon>
        <span>Repair Center</span>
      </template>
      <el-sub-menu v-if="isRcAppManagerUser" index="rc-pricing">
        <template #title>Pricing</template>
        <el-menu-item index="rc-pricing-services" :route="{ name: 'rc-pricing-services' }">
          <span>Services</span>
        </el-menu-item>
        <el-menu-item index="rc-pricing-repair-action" :route="{ name: 'rc-pricing-repair-actions' }">
          <span>Repair Charges</span>
        </el-menu-item>
      </el-sub-menu>
      <el-menu-item v-if="isRcAppManagerUser" index="rc-billing" :route="{ name: 'rc-billing' }">
        <span>Billing</span>
      </el-menu-item>
      <el-menu-item
        v-if="isRcAppManagerUser || isCustomer"
        index="rc-repair-request-submissions"
        :route="{ name: 'rc-repair-request-submissions' }"
      >
        <span>Repair Request Submissions</span></el-menu-item
      >
      <el-menu-item index="rc-work-orders" :route="{ name: 'rc-work-orders' }"> <span>Work Orders</span></el-menu-item>
      <el-sub-menu v-if="isRCAppUser" index="rc-logistics">
        <template #title>Logistics</template>
        <el-menu-item index="rc-logistics-shipments" :route="{ name: 'rc-logistics-shipments' }"
          ><span>Shipments</span></el-menu-item
        >
        <el-menu-item index="rc-logistics-containers" :route="{ name: 'rc-logistics-containers' }"
          ><span>Containers</span></el-menu-item
        >
      </el-sub-menu>

      <el-menu-item v-if="isRCAppUser" index="rc-miners-dashboard" :route="{ name: 'rc-miners-dashboard' }">
        <span>Miners</span>
      </el-menu-item>
      <el-menu-item v-if="isRCAppUser || isCustomer" index="rc-spare-parts" :route="{ name: 'rc-spare-parts' }">
        <span>Spare Parts</span></el-menu-item
      >
    </el-sub-menu>
    <el-sub-menu v-if="isCustomer || isASICWAppUser" index="asicw">
      <template #title>
        <el-icon>
          <Icon icon="mdi:contract-sign" />
        </el-icon>
        <span>ASIC Warranty</span>
      </template>
      <el-menu-item index="asicw-policy-groups" :route="{ name: 'asicw-policy-groups' }">Policy Groups</el-menu-item>
      <el-menu-item index="asicw-policies" :route="{ name: 'asicw-policies' }">Policies</el-menu-item>
      <el-menu-item index="asicw-pricing" :route="{ name: 'asicw-pricing' }">Pricing</el-menu-item>
      <el-menu-item index="asicw-claims" :route="{ name: 'asicw-claims' }">Claims</el-menu-item>
      <el-sub-menu v-if="isASICWManagerUser" index="asicw-billing">
        <template #title>Billing</template>
        <el-menu-item index="asicw-claim-invoices" :route="{ name: 'asicw-claim-invoices' }"
          >Claim Invoices</el-menu-item
        >
        <el-menu-item index="asicw-premium-invoices" :route="{ name: 'asicw-premium-invoices' }"
          >Premium Invoices</el-menu-item
        >
        <el-menu-item index="asicw-balance-sheet" :route="{ name: 'asicw-balance-sheet' }">Balance Sheet</el-menu-item>
      </el-sub-menu>
    </el-sub-menu>
    <el-sub-menu v-if="isHVLAppUser" index="hvl">
      <template #title>
        <el-icon>
          <OfficeBuilding />
        </el-icon>
        <span>Hosting</span>
      </template>
      <el-menu-item index="hvl-visitor-logs" :route="{ name: 'hvl-visitor-logs' }">Visitor Logs</el-menu-item>
      <el-menu-item index="hvl-hosted-miners" :route="{ name: 'hvl-hosted-miners' }">Hosted Miners</el-menu-item>
      <el-menu-item v-if="isHVLManagerUser" index="hvl-equipment-types" :route="{ name: 'hvl-equipment-types' }"
        >Equipment Types</el-menu-item
      >
      <el-menu-item index="hvl-equipment" :route="{ name: 'hvl-equipment' }">Equipment</el-menu-item>
    </el-sub-menu>
    <footer class="sidebar__footer" :class="!isCollapse && 'is-expanded'">
      <MiniProfile :is-collapse="isCollapse" />
      <el-button v-if="isCollapse" class="collapse-button footer-collapse-button" link @click="toggleSidebar">
        <Icon icon="material-symbols:left-panel-open-outline" height="20" />
      </el-button>
    </footer>
  </el-menu>
</template>

<style scoped lang="scss">
.sidebar {
  display: flex;
  flex-direction: column;
  background: var(--fill-secondary);
  height: 100vh;
  border-right: none;
  width: 64px;
  overflow-y: scroll;

  :deep(.el-sub-menu__title) {
    justify-content: center;
  }

  .collapse-button {
    padding: 0;
    color: var(--text-tertiary);
    position: absolute;

    &:hover {
      color: var(--text-primary);
    }
  }

  &:not(.el-menu--collapse) {
    width: 256px;

    :deep(.el-sub-menu__title) {
      justify-content: flex-start;
    }

    .collapse-button {
      right: calc(var(--el-menu-base-level-padding) - 5px);
    }
  }

  &__header {
    padding: var(--spacing-spacing-md);
    display: flex;
    align-items: center;
    justify-content: center;

    &.is-expanded {
      justify-content: space-between;
      padding-left: calc(var(--el-menu-base-level-padding) + var(--el-menu-level) * var(--el-menu-level-padding));
      padding-right: calc(var(--el-menu-base-level-padding) + var(--el-menu-icon-width));
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    flex: 1;
    padding: var(--spacing-spacing-md) var(--spacing-spacing-sm);

    .footer-collapse-button {
      position: fixed;
      bottom: 22px;
      left: 69px;
      z-index: 1999;
    }

    &.is-expanded {
      align-items: flex-start;
      padding: var(--spacing-spacing-md);
    }
  }
}
</style>
